import { Button, DatePicker, Drawer, Flex, Modal, Select, Table, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { useContext, useEffect, useState } from "react";
import LogsContext from "../../contexts/data/LogsContext";
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { logLevelAsString, logCategoryAsString } from "@leedpulse-monorepo/enums";
import { LogCategory, LogLevel } from "@leedpulse-monorepo/enums";
import dayjs from "dayjs";
import 'moment/locale/fr';
import moment from "moment";
import { ILog, ILoginAsUserLog, ISubscriptionLog, IAuthLog } from "@leedpulse-monorepo/interfaces";
moment.locale('fr');

const { RangePicker } = DatePicker;
type LogRow = ILog | ILoginAsUserLog | ISubscriptionLog | IAuthLog;

interface LogsMenuProps {
    isLogsOpened: { open: boolean, id?: string };
    onClose: () => void;
}

const LogsMenu: React.FC<LogsMenuProps> = ({ isLogsOpened, onClose }) => {

    const logsContext = useContext(LogsContext);

    const [filters, setFilters] = useState<{
        type: LogLevel | null;
        category: LogCategory | null;
        period: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    }>({
        type: null,
        category: null,
        period: null
    });

    const [selectedLog, setSelectedLog] = useState<LogRow | null>(null);

    const columns: TableColumnsType<LogRow> = [
        {
            align: 'center',
            title: 'Type',
            dataIndex: 'level',
            key: 'level',
            render: (value) => logLevelAsString(value)
        },
        {
            title: 'Catégorie',
            dataIndex: 'category',
            key: 'category',
            render: (value) => logCategoryAsString(value)
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (value) => moment(value).format('DD/MM/YYYY LTS')
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            render: (value, record) => (
                <div
                    style={{ maxWidth: 550, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onClick={() => setSelectedLog(record)}
                >
                    {value}
                </div>
            )
        },
        ...(filters.category === LogCategory.loginasuser
            ? [
                {
                    title: 'Admin UID',
                    dataIndex: 'adminUid',
                    key: 'adminUid',
                    render: (_value: unknown, record: LogRow) => record?.adminUid || '-'
                },
                {
                    title: 'Cible UID',
                    dataIndex: 'targetUserUid',
                    key: 'targetUserUid',
                    render: (_value: unknown, record: LogRow) => record?.targetUserUid || '-'
                }
            ]
            : []),
        ...(filters.category === LogCategory.auth
            ? [
                {
                    title: 'Email',
                    dataIndex: 'email',
                    key: 'email',
                    render: (_value: unknown, record: LogRow) => record?.email || '-'
                },
                {
                    title: 'Product',
                    dataIndex: 'product',
                    key: 'product',
                    render: (_value: unknown, record: LogRow) => record?.product || '-'
                }
            ]
            : [])
    ];

    useEffect(() => {
        logsContext.fetch();
    }, []);

    useEffect(() => {
        logsContext.fetch(filters);
    }, [filters])

    useEffect(() => {
        if (isLogsOpened.id) {
            logsContext.fetch({ id: isLogsOpened.id });
        }
    }, [isLogsOpened]);

    return (
        <div>
            <Drawer
                title="🗒️ Logs"
                width={925}
                placement={'right'}
                closable={false}
                onClose={() => onClose()}
                open={isLogsOpened.open}
                key={'right'}
                extra={
                    <Button type="primary" icon={<ReloadOutlined />} onClick={() => logsContext.fetch(filters)}>
                        Rafraîchir
                    </Button>
                }
                styles={{
                    body: {
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <h4>Filtres</h4>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                    <Tooltip title='Type'>
                        <Select
                            style={{ marginBottom: '2rem', width: 250 }}
                            value={filters.type}
                            onChange={(value) => setFilters({ ...filters, type: value })}
                            placeholder='Type...'
                            allowClear
                            options={[
                                {
                                    label: <Flex align="center"><CheckCircleOutlined style={{ marginRight: 6 }} /> Succès</Flex>,
                                    value: LogLevel.success
                                },
                                {
                                    label: <Flex align="center"><CloseCircleOutlined style={{ marginRight: 6 }} /> Erreur</Flex>,
                                    value: LogLevel.error
                                },
                                {
                                    label: <Flex align="center"><WarningOutlined style={{ marginRight: 6 }} /> Alerte</Flex>,
                                    value: LogLevel.warn
                                },
                                {
                                    label: <Flex align="center"><InfoCircleOutlined style={{ marginRight: 6 }} /> Information</Flex>,
                                    value: LogLevel.info
                                }
                            ]} />
                    </Tooltip>
                    <Tooltip title='Catégorie'>
                        <Select
                            style={{ marginBottom: '2rem', width: 250 }}
                            value={filters.category}
                            onChange={(value) => setFilters({ ...filters, category: value })}
                            placeholder='Catégorie...'
                            allowClear
                            options={Object.entries(LogCategory).map((key) => {
                                return {
                                    value: key[1],
                                    label: logCategoryAsString(key[1])
                                }
                            })} />
                    </Tooltip>
                    <Tooltip title='Période'>
                        <RangePicker
                            placeholder={['Début', 'Fin']}
                            format='DD/MM/YYYY HH:mm'
                            showTime={{ format: 'HH:mm' }}
                            onChange={(dates) => {
                                if (dates) {
                                    const [beginDate, endDate] = dates;
                                    setFilters({ ...filters, period: [beginDate, endDate] });
                                } else {
                                    setFilters({ ...filters, period: null });
                                }
                            }}
                            style={{ marginBottom: '2rem', width: 320 }} />
                    </Tooltip>
                </div>
                <Table<LogRow>
                    pagination={false}
                    columns={columns}
                    dataSource={logsContext.items} />
                <Modal
                    title="Détail du log"
                    open={!!selectedLog}
                    onCancel={() => setSelectedLog(null)}
                    footer={null}
                    width={700}
                >
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '60vh', overflow: 'auto', fontSize: 13 }}>
                        {JSON.stringify(selectedLog, null, 2)}
                    </pre>
                </Modal>
                {
                    logsContext.items.length > 0 && logsContext.items.length % 100 === 0 &&
                    <Button onClick={() => logsContext.fetchNext()}
                        style={{
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            marginTop: '2rem',
                            padding: '1rem'
                        }}>
                        Charger plus...
                    </Button>
                }
            </Drawer>
        </div>
    );
};

export default LogsMenu;
